import { g as generateSearchUrlBase } from './generate-search-url-base.js';
import { i as isBookingEnginePage } from './is-booking-engine-page.js';
import './index2.js';

/**
 * **iHotelier / Travelclick**
 *
 * @exports bs_3
 * @category engines
 */

function factory({ window, document, thn }) {

  const engine = {};
  const isRoveHotels = thn.data.accountUserId === 1042724;
  const HOTEL_MAHEKAI_PID = 1037812;
  const travelclickHostname = window.location.hostname.match(/travelclick/);
  const isFirstHotels = thn.data.accountUserId === 1044655;

  /**
     * Correspondences between THN promocode types (keys)
     * and Travelclick promocode types (values).
     */
  const promocodesMap = {
    // Supported/activated
    default: 'Discount',
    group: 'Group',
    corpo: 'Corporate',
    // To be standarized:
    // '<unknown>': 'TravelAgent',
  };

  engine._getAngularElementScope = function (selector) {

    const elem = document.querySelector(selector);
    const scope = window.angular?.element(elem)?.scope();

    return scope;
  };

  engine._getPromocodeScope = () => engine._getAngularElementScope('.codeWidget');
  engine._getGlobalScope = () => engine._getAngularElementScope('html');
  engine._getExternalCodeType = () => engine._getGlobalScope()?.RatesAvail?.roomStays?.[0]?.ratePlans?.[0]?.rateExternalCode;
  engine._getCurrentGlobalScopePromoId = () => engine._getGlobalScope()?.RatesAvail?.roomStays?.[0]?.ratePlans?.[0]?.merchandisedPromoData?.promotionId
  || engine._getExternalCodeType();

  let initialPromotionId = null;

  /**
     * @memberOf module:bs_3
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot(ctx) {

    /**
      * General applicaton of promocodes for Travelclick is done via GET.
      *
      * Alternatively, the promocode application can be done via "function call" too.
      * For that, we define the global function `thn.applyTravelclickPromocode()`,
      * only in the BE pages. We detect BE by checking the `angular` var.
      * The function tries to apply the promocode using scoped angular methods (POST).
      * Fallsback to change window.location if conditions for POST are not met.
      */

    const onPageChange = ({ pageName }) => {
      // eslint-disable-next-line camelcase
      if (pageName === 'Rooms and Rates' && initialPromotionId === null) {
        // if promocode input is empty check if there is any promotion applied by default ant store the id
        // when calls.length is truthy then we can not trust getCurrentGlobalScopePromoId because we can not determine
        // if it's the default code applied or is the one entered on calendar
        initialPromotionId = engine._getPromocodeScope()?.codes?.length === 0 ? engine._getCurrentGlobalScopePromoId() : undefined;
      }
    };

    thn.bus.on('thn:page.set', onPageChange);
    thn.shutdown(() => { thn.bus.detach('thn:page.set', onPageChange); });

    if (travelclickHostname) {
      const hadlerButtonUpdate = thn.delegate('click', '#currency-dropdown-update-button-id', () => {
        setTimeout(() => {
          thn.languageCheck();
          if (thn.data.pageName === 'Rooms and Rates') {
            thn.widgets.price.refresh();
          }
        }, 1200);
      });
      thn.shutdown(() => {
        hadlerButtonUpdate();
      });
    }

    if (window.angular) {
      const debugPrefix = 'thn.applyTravelclickPromocode()';

      thn.logger.debug('boot()', 'Defining a custom function to apply promocodes:', debugPrefix);

      thn.applyTravelclickPromocode = function thnApplyTravelclickPromocode({ code, type }) {

        // this force type to corpo for hotels that have the setting 'engine_discount_type_force_corpo' enabled in table `property.settings`.
        if (thn.getSetting('engine_discount_type_force_corpo')) {
          type = 'corpo';
        }

        // Get the angular scope
        const scope = engine._getPromocodeScope();

        // Let's find the corresponding Travelclick type, from THN type.
        // Only one THN type will not have a correspondence: "rate".
        // Rate is a *magic* parameter in Travelclick to be applied only via GET
        // Example: https://reservations.hotelbelami-paris.com/107742?RatePlanId=3808139
        let travelclickType = promocodesMap[type];

        // If we have the angular "scope", then we are inside Travelclick BE
        // so can apply the promocode via POST (except if don't know the type)
        if (scope && travelclickType) {
          // Check if the type is acceptable in this very case or go with the default

          if (!scope.codeTypes.map((t) => t.name).includes(travelclickType)) {
            travelclickType = promocodesMap.default;
          }

          scope.codeType = travelclickType;
          scope.code = code;
          scope.addCode();
          thn.logger.debug(debugPrefix, 'Applied promocode via POST');
          return;
        }

        // At this point we can't apply the promocode via POST
        // let's programmatically go via GET

        if (!thn.data.promocodeVars[type]) {
          thn.logger.warn(debugPrefix, `No valid promocode vars for type "${type}"`);
          return;
        }

        const discountData = {
          discount: {
            value: code,
            kind: type,
          },
        };
        const replayPromoUrl = thn.createSearchReplay(discountData)?.replayUrl;
        if (replayPromoUrl) {
          thn.logger.debug(debugPrefix, 'Applied promocode via GET:', replayPromoUrl);
          window.location.href = replayPromoUrl;
          if (window.location.hash) {
            window.location.reload();
          }
          return;
        }

        thn.logger.warn(debugPrefix, 'Unable to apply the promocode:', code);
      };

      thn.shutdown(function () {
        delete thn.applyTravelclickPromocode;
      });
    }

    /*
            2019-06-03 / Conditional updated: Add the observer to the whole BE in order to check lang changes.
        */

    const isUsersUsingHTMLObserver = [1038104, 1041265, 1045866, 104308, 1040793].includes(thn.data.accountUserId);

    if ((window.location.href.match(/reservations(\.[^/]+){2,}\/\d+/))
      || window.location.hostname.match(/bookings\.falkensteiner/) || thn.data.propertyId === 1026619 || isUsersUsingHTMLObserver) {

      const htmlEl = document.getElementsByTagName('html')[0];
      if (htmlEl && !thn.travelclickLangUpdater) {
        thn.travelclickLangUpdater = function thnTravelclickLangUpdater() {
          const loader = document.querySelector('#loader');
          if (!thn.isVisible(loader)) {
            setTimeout(() => {
              thn.languageCheck();
              thn.pageCheck();
              if (thn.data.pageName === 'Rooms and Rates') {
                thn.widgets.price.refresh();
              } // in order to detect change from Calendar to Rooms and Rates for Hotel Falkensteiner
            }, 2000);
          }
        };

        thn.observeDom(htmlEl, thn.travelclickLangUpdater, {
          childList: true,
          subtree: false,
          characterData: false,
          attributes: true,
        });

        thn.shutdown(function () {
          thn.stopObserveDom(htmlEl, thn.travelclickLangUpdater);
          delete thn.travelclickLangUpdater;
        });
      }
    }

    // Different index for summer and winter
    // Temmos group
    if ([1016772, 1016774].includes(thn.data.propertyId)) {

      const target = document.body;
      const seasonLoader = document.getElementsByClassName('siteHeader-season')[0];
      const seasonSwitcher = document.getElementsByClassName('js-seasonSwitcher')[0];

      if ((seasonLoader || seasonSwitcher) && !thn.temmosLoaderObv) {
        // Define page update function
        thn.temmosLoaderObv = thn.debounce(function thnTemmosLoaderObv() {
          thn.languageCheck();
          thn.pageCheck();
        }, 200);

        thn.observeDom(target, thn.temmosLoaderObv, {
          attributes: true,
          attributesFilter: ['class'],
        });

        thn.shutdown(function () {
          thn.stopObserveDom(target, thn.temmosLoaderObv);
          delete thn.temmosLoaderObv;
        });
      }
    }

    /*
    Some users want to show notes when a client is logged in but as there is no page reload the notes do not disappear
    */
    const accountsLoginChange = [1033913, 1044655, 1041265];
    const dataLayer = window.dataLayer;
    const isInBookingEngine = window.location.hostname.match(/bookings.travelclick|reservations.firsthotels.com|bookings.castillatermal/);

    if (accountsLoginChange.includes(thn.data.accountUserId) && dataLayer && isInBookingEngine) {
      // eslint-disable-next-line no-unused-vars
      let travelclickLoginObserver = new thn.DataLayerHelper(window.dataLayer, function (model, message) {
        if (message?.loggedIn) {
          thn.pageCheck({ force: true });
        }
      }, true);
      thn.shutdown(function () {
        travelclickLoginObserver.cancel();
        travelclickLoginObserver = null;
      });
    }
  };

  // This serves as a promocode validation, if a invalid promocode is entered the promoId does not change
  engine._hasPromotionApplied = () => {

    if (initialPromotionId === null) {
      return false;
    }

    return initialPromotionId !== engine._getCurrentGlobalScopePromoId();
  };

  /**
     * @memberOf module:bs_3
     * @this thn.widgets.price
     * @see {@link Price#get_promocode}
     *
     * @param {object} ctx - The context object.
     */
  engine.getPromocode = function getPromocode(ctx) {

    const promocodeScope = engine._getPromocodeScope();
    const datalayerPromocode = thn.dataLayerHelper.get('ihDiscountCode') || thn.dataLayerHelper.get('ihAccessCode');

    if (promocodeScope) {
      // We can extract the currently applied promocode from the Angular scope.
      // Unfortunatelly the value of the `scope.codeType` does NOT denote the type
      // of that promocode, but the current type selected in the UI dropdown.
      // Since there is no reliable way to detect the promocodeType from here,
      // we do not define `ctx.promocodeType` (as we should).
      const promocode = promocodeScope.code || promocodeScope.codes?.[0];
      const typePromo = thn.engine._getGlobalScope()?.RatesAvail?.specialCodes?.type;

      // If the promotion type is 'CorporateId', we apply the promotion directly, as this type
      // of promotion does not allow the application of additional valid codes.
      ctx.promocode = (engine._hasPromotionApplied() && promocode) || (typePromo === 'CorporateId' && promocode) || (datalayerPromocode && promocode) || '';

      return ctx.promocode;
    }

    ctx.promocode = (engine._hasPromotionApplied() && datalayerPromocode) || '';

    return ctx.promocode;
  };

  /**
     * Forbid execution on:
     * - Maquis Los Cabos (1014020)
     *
     * @memberOf module:bs_3
     * @see {@link allowed_location}
     *
     * @param {object} ctx - The context object.
     */
  engine.allowedLocation = function allowedLocation(ctx) {

    let isAllowedLocation = true;

    // Maquis Los Cabos
    if (thn.data.propertyId === 1014020 && !!thn.parseUrlParameter('identifier')) {
      isAllowedLocation = false;
    }

    return isAllowedLocation;
  };

  /**
     * @memberOf module:bs_3
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {

      let lang;
      const ihLanguages = {
        1: 'en',
        2: 'es',
        3: 'fr',
        4: 'pt',
        5: 'zh',
        6: 'ja',
        7: 'de',
        8: 'it',
        15: 'ar',
        29: 'he',
      };

      const sanaLangs = {
        English: 'en',
        Português: 'pt',
        Deutsch: 'de',
        Français: 'fr',
        Español: 'es',
        Русский: 'ru',
      };

      const dataLayer = window.dataLayer;
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      const pacuarelodgePid = 1035293;
      const coastUid = 1036402;

      if (thn.data.propertyId === pacuarelodgePid || thn.data.accountUserId === coastUid) {
        resolve('en');
        return;
      }

      if (thn.data.propertyId === 1016661 && hostname.match(/welcomeinn/)) {
        lang = document.querySelector('.wpml-ls-current-language')?.textContent?.trim();
        lang = lang && sanaLangs[lang];
        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      if (thn.data.propertyId === 1048394 && hostname.match(/perapalace/)) {
        const langText = document.querySelector('.lng .active')?.textContent?.toLowerCase();
        lang = langText === 'cn' ? 'zh' : langText;

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // The View Rabat, The View Agadir & The View Bouznika
      // To detect lang of the website, not BE.
      if ([1014411, 1024955, 1024957].includes(thn.data.propertyId) && !hostname.match(/reservations/)) {
        lang = pathname.split('/')[1];
        resolve(lang);
        return;
      }

      //* ****// General language case for BE
      lang = document.documentElement.lang || thn.dataLayerHelper.get('pageLanguage');

      // For this user the dataLayer pageLanguage variable says wrongly 'Northern Sami'
      if ([1036266, 1038104].includes(thn.data.accountUserId) && lang === 'se') {
        lang = 'sv';
      }

      // Glh Hotels Chain & Eagle Aruba Resort and Casino && First hotels
      if ([1034117, 1043606, 1044655].includes(thn.data.accountUserId)) {
        lang = document.documentElement.lang;
        if (isFirstHotels && (lang === 'nb' || lang === 'se')) {
          lang = lang === 'nb' ? 'no' : 'sv';
        }
        if (lang) {
          resolve(lang);
          return;
        }
      }

      if (ctx.langs.test(lang)) {
        resolve(lang);
        return;
      }

      // Grand Ferdinand Hotel
      if (thn.data.propertyId === 1016976) {
        const html = document.querySelector('html');
        lang = html && html.getAttribute('lang');

        if (lang === 'du-DE') {
          resolve('de');
          return;
        }

        // Gulasch & Söhne - Restaurant
        if (hostname.match(/gulaschundsoehne/)) {
          lang = pathname.match(ctx.urlRe);

          if (lang && lang[1] && ctx.langs.test(lang[1])) {
            resolve(lang);
            return;
          }

          resolve('de');
          return;
        }
      }

      // Divan hotels
      if ([1036055, 1035221].includes(thn.data.accountUserId) && hostname.match(/divan|salonmarie/)) {
        const dataLang = document.querySelector('[data-lang]');
        lang = dataLang && dataLang.getAttribute('data-lang');

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // Penta Hotels
      if (thn.data.accountUserId === 1035380 && !hostname.match(/reservations/)) {
        lang = (document.querySelector('.metalink.lang_nav_trigger') || {}).textContent;
        if (lang) {
          if (lang === 'cz') {
            resolve('cs');
          } else if (lang.match(/cn|hk|繁體中文/)) {
            resolve('zh');
          } else {
            resolve(lang);
          }
          return;
        }
      }

      // Sana Hotels
      if (thn.data.accountUserId === 1032355 && !isBookingEnginePage(thn.data.pageName)) {
        lang = document.querySelector('.lang--active');
        lang = lang && lang.title && sanaLangs[lang.title];

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // Amerian hoteles
      if ([1015245, 1015244].includes(thn.data.propertyId)) {
        lang = hostname.match(/(portugues|english|french)/i);
        if (lang) {
          lang = lang[1];
          switch (lang) {
            case 'portuguesa':
              resolve('pt');
              return;
            case 'english':
              resolve('en');
              return;
            case 'french':
              resolve('fr');
              return;
            default:
              resolve('es');
              return;
          }
        } else {
          resolve('es');
          return;
        }
      }

      // Rixos Hotels
      if (thn.data.accountUserId === 1034422 && dataLayer) {
        thn.domReady().then(function () {
          lang = thn.dataLayerHelper.get('language');
          lang = lang && lang.toLowerCase();

          if (lang && ctx.langs.test(lang)) {
            resolve(lang);
            return;
          }
          reject('no_language');
        });
        return;
      }

      // mahekalbeachresort
      if (thn.data.propertyId === 1014441) {
        if (pathname === '/startseite') {
          resolve('de');
          return;
        } if (pathname === '/inicio') {
          resolve('es');
          return;
        }
        resolve('en');
        return;
      }

      // Hotel Kungsträdgården
      // First hotels
      if ([1035444, 1033777, 1044655].includes(thn.data.accountUserId)) {

        lang = ((document.querySelector('.current-lang') || {}).textContent || '');

        lang = (lang === 'se') ? 'sv' : lang;

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      lang = thn.parseUrlParameter('languageid') || thn.parseUrlParameter('languageId');
      if (lang) {
        lang = lang && ihLanguages[lang];

        if (ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // Check baseRes
      if (window.baseRes && window.baseRes.languageCode) {
        lang = window.baseRes.languageCode.substr(0, 2).toLowerCase();

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // wilton hotel buenos aires
      if (thn.data.propertyId === 1012765 && hostname === '/') {
        resolve('es');
        return;
      }

      // Hotel Charleston Santa Teresa (PID->1016873)
      if (thn.data.propertyId === 1016873) {
        lang = (document.querySelector('.tc-lang-header') || {}).title;

        if (lang === 'Español') {
          lang = 'es';
        } else {
          lang = 'en';
        }

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // commmon by url
      lang = pathname.match(/\/(es|fr|en|pt|de|ru|it|bra|mx|jp|ar|cn)(\/|$)/i);
      if (lang) {
        lang = lang[1];
        switch (lang) {
          case 'bra':
            resolve('pt');
            return;
          case 'mx':
            resolve('es');
            return;
          case 'jp':
            resolve('ja');
            return;
          case 'cn':
            resolve('zh');
            return;
          default:
            resolve(lang);
            return;
        }
      }

      // Mision hoteles
      if ([1011173, 1011174].includes(thn.data.propertyId)) {
        if (pathname.match(/^\/e_/)) {
          resolve('en');
        } else {
          resolve('es');
        }
        return;
      }

      // Aryaduta Hotels
      lang = thn.data.accountUserId === 1032691
        && pathname.match(/-(\w{2})\.html$/);
      if (lang && ctx.langs.test(lang[1])) {
        resolve(lang[1]);
        return;
      }

      // La Colección hotels (related to Posadas)
      if (hostname === 'www.lacoleccionresorts.com') {
        resolve('en');
        return;
      }

      // Explorean.com
      lang = pathname.match(/_(\w{2}).html/i);
      lang = lang && lang.length && lang[1];
      if (lang && ctx.langs.test(lang)) {
        resolve(lang);
        return;
      }

      // Case where, at the language selector, the language is not translated to english, it's on its own language
      // Australis Hotels (Costa Australis & Cabo de Hornos) & St. George Lycabettus Hotel
      if ([1035506, 1035546].includes(thn.data.accountUserId)) {
        lang = (document.querySelector('.tc-lang-header') || '').textContent;

        if (lang === 'Español') {
          lang = 'es';
        } else if (lang === 'ΕΛΛΗΝΙΚΑ') {
          lang = 'el';
        }

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // Aranwa Pueblito Encantado del Colca,Aranwa Paracas Resort & Spa,
      // Aranwa Vichayito Bungalows & Carpas
      if ([1014802, 1014803, 1014804].includes(thn.data.propertyId)) {
        lang = (document.querySelector('.header-menu--banner .header-menu__select.show-lg span') || {}).textContent || '';

        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      // Hotel Plaza Grande

      if (thn.data.propertyId === 1024981 && !(hostname.match(/travelclick/))) {
        // I used hostname instead of a selector, because the website doesn't have one selector,
        // but two separated links buttons that redirect to 2 sites: one in Spanish and one in English
        if (window.location.hostname.match(/hotelplazagrandeingles/)) {
          resolve('en');
          return;
        }
      }

      if (thn.data.propertyId === 1019468) {
        const mayflowerLangcodes = {
          spanish: 'es',
          chinese: 'zh',
          french: 'fr',
        };
        lang = window.location.pathname.match(/^\/(spanish|chinese|french)/);
        lang = lang && mayflowerLangcodes[lang[1]];
        if (lang && ctx.langs.test(lang)) {
          resolve(lang);
          return;
        }
      }

      const paradisevillagePattern = /paradisevillage\.com\.mx\S*(movil|mobile)/;
      if (thn.data.propertyId === 1035935 && paradisevillagePattern.test(window.location)) {
        lang = window.location.pathname.includes('movil') ? 'es' : 'en';
        resolve(lang);
        return;
      }

      thn.domReady().then(function () {

        const $ = window.jQuery;

        if (window.location.host.match(/grandfiestamericana/)) {
          lang = $ && $('.country-selector i').attr('class');
          if (lang) {
            resolve(lang === 'es' ? 'en' : 'es');
            return;
          }

          resolve('es');
          return;
        }

        // legacy
        lang = document.getElementById('select-language');

        if (lang) {
          lang = (lang.options || {})[lang.selectedIndex];
          lang = lang.getAttribute && lang.getAttribute('value');
          lang = lang && ihLanguages[lang];

          if (ctx.langs.test(lang)) {
            resolve(lang);
            return;
          }
        }

        // next generation travelclick (angularjs)
        lang = document.querySelector
          && document.querySelector('select[ng-model=currentLanguage]');

        if (lang) {
          lang = (lang.options || {})[lang.selectedIndex];
          lang = lang.getAttribute && ((lang.getAttribute('value') || '').substr(7, 2) || '').toLowerCase();

          if (ctx.langs.test(lang)) {
            resolve(lang);
            return;
          }
        }

        // hoteles CUMBRES
        lang = document.querySelector
          && document.querySelector('a.tc-lang-header');
        if (lang && thn.data.accountUserId === 1034096) {
          lang = lang.text;
          switch (lang) {
            case 'Español':
              resolve('es');
              return;
            default:
              resolve(lang);
              return;
          }
        }

        // Hotels Camino real
        lang = document.querySelector('.nav-lenguages a.active');
        if (lang && thn.data.accountUserId === 1038495) {
          if (lang.textContent === 'Esp') {
            resolve('es');
            return;
          }
          resolve('en');
          return;
        }
        reject('no_language');

      });
    });
  };

  /**
     * @memberOf module:bs_3
     * @see {@link is_user_logged}
     *
     * @param {object} ctx - The context object.
     */
  engine.isUserLogged = function isUserLogged(ctx) {

    let isLogged = window.dataLayer && thn.dataLayerHelper.get('loggedIn', { first: false }) === 'yes';

    // Check if we are in the homepage of Grupo Posadas
    const hostname = window.location.hostname;
    const hostnameRegex = /fiesta|onehoteles|gammahoteles|liveaqua|explorean/;
    if (hostname.match(hostnameRegex) && !hostname.match(/reservations/)) {
      isLogged = !!document.querySelector('.logged');
    }
    // First Hotels
    if (isFirstHotels) {
      isLogged = !!(thn.isVisible(document.querySelector('#logout')) || thn.objectGet(window.dataLayer, 'loggedIn', { first: false }) === 'yes');
    }

    return isLogged;

  };

  engine._getRateBlacklistRegexp = function () {
    // To avoid adding a filtering in multiple lines of code the properties that should exclude taxes we use this function
    let regexBlacklist = 'AAA|AARP|GOV|Military|Law Enforcement|Senior|Veteran';
    if ([1015573, 1025496, 1023311, 1026924, 1027093, 1014051].includes(thn.data.propertyId)) {
      regexBlacklist += '|Florida Resident';

      if (thn.data.propertyId === 1027093) {
        regexBlacklist += '|Disney Annual';
      } else if (thn.data.propertyId === 1014051) {
        regexBlacklist += '|Suite Art Deal';
      }

    } else if ([1026514].includes(thn.data.propertyId)) {
      regexBlacklist += '|Corporate';
    } else if ([1015000, 1014992].includes(thn.data.propertyId)) {
      regexBlacklist += '|gou?verne?ment';
    } else if ([1035644].includes(thn.data.accountUserId)) {
      regexBlacklist += '|Qualified discount';
    } else if (thn.data.propertyId === 1035381) {
      regexBlacklist += '|State Government|Government Rate';
    } else if ([1047662].includes(thn.data.accountUserId) && document.querySelector('.multirate-pricelist-rate-title')) {
      regexBlacklist += '|^(?!Room Only).*$'; // Exclude all rates except 'Room Only'
    } else if ([1036660].includes(thn.data.accountUserId)) {
      regexBlacklist += '|Advance Purchase Promotion';
    }
    return regexBlacklist ? new RegExp(regexBlacklist, 'i') : null;
  };

  engine._isSpecialRateParam = function () {
    const specialParams = {
      1032273: [
        '16789',
        '16790',
        '16788',
        '16785',
        '16787',
      ],
    };

    const params = (new URL(window.location)).searchParams;
    return (specialParams[thn.data.propertyId] || []).includes(params.get('roomcategoryid'));
  };

  /**
     * @memberOf module:bs_3
     * @see {@link onHashChange}
     *
     * @param {object} ctx - The context object.
     */
  engine.onHashChange = function onHashChange(ctx) {

    // reset PW when coming from Calendar to evaluate "awaits_pw" type of popup widgets only once parsing is done
    if (thn.data.pageName === 'Calendar') {
      thn.widgets.price.reset();
    }
    const loader = document.querySelector('#loader');
    if (loader && !thn.TravelClickLoaderObserver) {
      thn.TravelClickLoaderObserver = thn.debounce(function thnTravelClickLoader() {
        if (!thn.isVisible(loader)) {
          thn.languageCheck();
          thn.pageCheck();
        }
      }, 800);

      thn.observeDom(loader, thn.TravelClickLoaderObserver, {
        attributes: true,
      });

      thn.pageUnload(() => {
        thn.TravelClickLoaderObserver.cancel();
        thn.stopObserveDom(loader, thn.TravelClickLoaderObserver);
        delete thn.TravelClickLoaderObserver;
      });
      thn.pageCheck();
    } else {
      thn.pageCheck();
    }
  };

  /**
     * @memberOf module:bs_3
     * @see {@link onStateChange}
     *
     * @param {object} ctx - The context object.
     */
  engine.onStateChange = thn.debounce(() => {
    thn.pageCheck();
    thn.languageCheck();
  }, 400);

  /**
     * @memberOf module:bs_3
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    const url = new URL(ctx.baseUrl);

    // example 1: https://reservations.firsthotels.com/103114?Rooms=1&partner=3613&DateIn=11/14/2023&DateOut=11/17/2023&adults=2&children=1&languageid=en&rooms=1&currency=SEK#/accommodation/room
    // example 2: https://bookings.seminaris.de/108183?identifier=&confirmid=&DateIn=12/12/2023&DateOut=12/14/2023&adults=2&children=0&languageid=en&rooms=1&currency=EUR#/accommodation/room
    // We'll always have a numeric hotel identifier as the URL path
    const validUrl = !!url.pathname.match(/^\/\d+(\/|$)/); // Rooms and Rates

    if (!validUrl) {
      return { newUrl: '', newData: {} };
    }

    return generateSearchUrlBase(ctx);
  };

  /**
     * @memberOf module:bs_3
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    let stopObservation;
    let isWidgetSet = false;

    // on any case force a reset on price
    ctx.widget.reset();

    // PW hide & show events - fired when selecting specific rooms in the DOM
    // This function is called both when the thn.travelclick_observer is triggered and when rooms are already shown on load
    const handlePwEvents = () => {
      const event = thn.data.media.name === 'mobile' ? 'touchend' : 'click';
      const roomsGridActionSniffer = function (model, message) {
        if (message.action === 'ClickRoomWithTag' && ctx.widget.isVisible()) {
          ctx.widget.hide();
        }
      };

      if (window.dataLayer && (!thn.travelclickDatalayerHelper || !thn.travelclickShowPwUndelegate)) {

        thn.travelclickDatalayerHelper = new thn.DataLayerHelper(window.dataLayer, roomsGridActionSniffer, true);

        thn.travelclickShowPwUndelegate = thn.delegate(event, '.RoomDetail-close-button', () => {
          if (!ctx.widget.isVisible()) {
            ctx.widget.show();
          }
        }, true);

        thn.pageUnload(function () {
          delete thn.travelclickDatalayerHelper;
          delete thn.travelclickShowPwUndelegate;
        });
      }
    };

    const refreshWidgetTimeout = () => {
      setTimeout(function () {
        if (!ctx.widget.isSet() && ctx.widget.refresh() && !ctx.widget.isSet()) {
          ctx.widget.hide();
        }
      }, 3000);
    };

    // if no prices observe some time in case they show up
    if (!thn.travelclickObserver
      && !Array.from(document.querySelectorAll('.CardList-price-title')).filter(thn.isVisible).length) {

      this.logger.debug('************** Still no prices, observe *****************');

      stopObservation = function travelclickStopObservation() {
        ctx.widget.logger.debug('************** Shutdown prices observer *****************');
        clearInterval(thn.travelclickObserver);
        thn.travelclickObserver = null;
        thn.data.travelclickStartTime = null;
        isWidgetSet = false;
      };

      thn.data.travelClickStartTime = Date.now();
      thn.travelclickObserver = setInterval(function () {
        // as soon as rooms data is present stop observation
        if (!ctx.widget.isSet() && !isWidgetSet) {
          isWidgetSet = true;
          ctx.widget.setData();
          isWidgetSet = false;
        }
        if (ctx.widget.isSet()) {
          stopObservation();
          ctx.widget.remove(false); // just in case other observers showed a price meanwhile
          ctx.widget.show();
          handlePwEvents();
          // timeout on 15s
        } else if (Date.now() - thn.data.travelclickStartTime > 15000) {
          stopObservation();
        }

      }, 1000);

      thn.pageUnload(stopObservation);
      thn.pageUnload(ctx.widget.reset);
    }

    if (!thn.travelclickUndelegate && thn.isVisible(document.querySelector('.Intl-DropDown-btns-update'))) {

      thn.travelclickUndelegate = thn.delegate('click', '.Intl-DropDown-btns-update', function onChangeIntl(e) {

        const el = document.querySelector('.AccommodationsGrid');
        let refresh;

        ctx.widget.logger.debug('************** Travelclick localization change *****************');

        refresh = thn.debounce(function ihotelierRefresh() {
          thn.stopObserveDom(el, refresh);
          refresh = null;
          thn.languageCheck().then(function () {
            // Give the BE a few seconds to change the prices
            refreshWidgetTimeout();
          });
        }, 600); // important: never go below 400ms, DOM needs time to be updated
        thn.observeDom(el, refresh);
        // timeout DOM observation after Intl change
        setTimeout(function cleanupRefresh() {
          if (refresh) {
            thn.stopObserveDom(el, refresh);
            refresh = null;
          }
        }, 15000);
      });

      thn.pageUnload(function () {
        thn.travelclickUndelegate();
        delete thn.travelclickUndelegate;

        if (thn.travelclickShowPwUndelegate) {
          thn.travelclickShowPwUndelegate();
          delete thn.travelclickShowPwUndelegate;
        }
      });

      handlePwEvents();
    }

    // New header version. '.Intl-DropDown-btns-update' is hidden
    const newHeaderCurrencyEl = document.querySelector('currency-bar > .Intl-DropDown');
    const requiresCurrencyUpdater = !thn.travelclickCurrencyUpdater && newHeaderCurrencyEl;

    if (requiresCurrencyUpdater && !travelclickHostname) {
      thn.travelclickCurrencyUpdater = thn.debounce(function thnTravelclickCurrencyUpdater() {
        ctx.widget.logger.debug('* Travelclick Currency  Updater *');
        ctx.widget.refresh();
      }, 800);
      thn.observeDom(newHeaderCurrencyEl, thn.travelclickCurrencyUpdater, {
        childList: false,
        subtree: true,
        characterData: false,
        attributes: true,
      });
      thn.pageUnload(function () {
        thn.stopObserveDom(newHeaderCurrencyEl, thn.travelclickCurrencyUpdater);
        delete thn.travelclickCurrencyUpdater;
      });
    } else if (requiresCurrencyUpdater && travelclickHostname) {
      const container = document.querySelector('html');

      if (container) {
        thn.travelclickCurrencyUpdater = thn.debounce(function () {
          thn.parseCurrency();

          if (thn.data.pageName === 'Rooms and Rates') {
            thn.widgets.price.refresh();
          }
        }, 1000);

        thn.observeDom(container, thn.travelclickCurrencyUpdater, {
          childList: true,
          subTree: true,
          attributes: true,
        });

        thn.shutdown(() => {
          thn.travelclickCurrencyUpdater.cancel();
          thn.stopObserveDom(container, thn.travelclickCurrencyUpdater);
          delete thn.travelclickCurrencyUpdater;
        });
      }
    }

    const promocodeSelector = document.getElementById('add-code-dropdown-id');

    if (promocodeSelector
      && !thn.travelclickPromocodeUpdater && !isRoveHotels) {

      thn.travelclickPromocodeUpdater = thn.debounce(function thnTravelclickPromocodeUpdater() {
        ctx.widget.logger.debug('* Travelclick Promocode Updater (delayed)*');
        thn.debounce(function () {
          if (ctx.widget.refresh() && !ctx.widget.isSet()) {
            ctx.widget.hide();
          }
        }, 500);
      }, 600);

      thn.observeDom(promocodeSelector, thn.travelclickPromocodeUpdater, {
        childList: true,
        subtree: false,
        characterData: false,
        attributes: false,
      });

      thn.pageUnload(function () {
        thn.stopObserveDom(promocodeSelector, thn.travelclickPromocodeUpdater);
        delete thn.travelclickPromocodeUpdater;
      });
    }
    if (thn.data.media.name === 'mobile') {
      // Observer for the mobile version of TravelClick
      // This triggers a PW update everytime the screen loader is visible,
      // which happens everytime a price is change (in the dropdown) or
      // even if we change the currency or the language.
      let loader;

      if (!thn.travelclickMobileUpdate) {

        loader = document.getElementsByClassName('loader-screen')[0];
        if (loader) {

          // define update function
          thn.travelclickMobileUpdate = thn.debounce(function thnTravelclickMobileUpdate() {
            if (!thn.isVisible(loader)) {
              // try to refresh the PW data, then hide only if PW data is not set
              // Give the BE a few seconds to change the prices
              refreshWidgetTimeout();
            }
          }, 250);

          // start observation on loader
          thn.observeDom(loader, thn.travelclickMobileUpdate, {
            childList: false,
            subtree: false,
            characterData: false,
            attributes: true,
            attributeFilter: ['style', 'class'],
          });

          // setup unload
          thn.pageUnload(function () {
            thn.stopObserveDom(loader, thn.travelclickMobileUpdate);
            delete thn.travelclickMobileUpdate;
          });
        }
      }
    }

    // Hide the PW when room details are displayed
    if (!thn.travelclickRoomDetailsUpdate
      && thn.data.accountUserId === 1034117 // GLH Hotels
    ) {
      const roomDetailsSelector = '.AccommodationsGrid-roomDetail .AccommodationsGrid-Detail';
      const roomDetailsEls = Array.from(document.querySelectorAll(roomDetailsSelector));

      if (roomDetailsEls.length) {
        thn.travelclickRoomDetailsChecker = function thnTravelclickRoomDetailsUpdate() {
          return document.querySelectorAll(`${roomDetailsSelector}[aria-expanded="true"]`).length > 0;
        };

        // Initial state
        thn.data.thnTravelclickRoomDetailsVisible = thn.travelclickRoomDetailsChecker();

        thn.travelclickRoomDetailsUpdate = thn.debounce(function thnTravelclickRoomDetailsUpdate() {
          if (thn.travelclickRoomDetailsChecker()) {
            // Room Detail just show
            thn.data.thnTravelclickRoomDetailsVisible = true;

            // Hide PW
            ctx.widget.hide();
          } else if (thn.data.thnTravelclickRoomDetailsVisible) {
            // Room Detail just hide
            thn.data.thnTravelclickRoomDetailsVisible = false;

            // Show PW
            if (ctx.widget.refresh() && !ctx.widget.isSet()) {
              ctx.widget.hide();
            }
          }
        }, 250);

        const updateConfig = {
          childList: false,
          subtree: false,
          characterData: false,
          attributes: true,
          attributeFilter: ['style', 'class'],
        };

        // Start observation on every room detail element
        roomDetailsEls.forEach((roomDetailEl) => {
          thn.observeDom(roomDetailEl, thn.travelclickRoomDetailsUpdate, updateConfig);
        });

        thn.pageUnload(function () {
          // Stop observation on every room detail element
          roomDetailsEls.forEach((roomDetailEl) => {
            thn.stopObserveDom(roomDetailEl, thn.travelclickRoomDetailsUpdate, updateConfig);
          });

          delete thn.travelclickRoomDetailsUpdate;
          delete thn.data.thnTravelclickRoomDetailsVisible;
        });
      }
    }

    /**
         * Refresh PW when the user changes the type of rate (VAT, AA, AARP,...)
         *
         * Afected properties:
         *  - The Hayes Street Hotel
         *  - Fashion Island Hotel Newport Beach
         *
         * When we don't exclude rates (rates_blacklist), we get all the prices (even of the other rates)
         * and we don't need to refresh the PW.
         * But when we exclude rates , we only we the visible prices (the prices of the selected rate), for that reason,
         * we need to refresh the PW
         */
    if (document.querySelector('#AccommodationsGridId') && engine._getRateBlacklistRegexp()) {
      if (!thn.travelclickBlacklistedRatesPriceUpdater) {
        thn.travelclickBlacklistedRatesPriceUpdater = thn.debounce(function () {
          ctx.widget.refresh();
        }, 500);
      }

      thn.observeDom(document.querySelector('#AccommodationsGridId'), thn.travelclickBlacklistedRatesPriceUpdater, {
        childList: true,
        subtree: false,
        attributes: false,
      });

      thn.pageUnload(function () {
        thn.stopObserveDom(document.querySelector('#AccommodationsGridId'), thn.travelclickBlacklistedRatesPriceUpdater);
        delete thn.travelclickBlacklistedRatesPriceUpdater;
      });
    }

    // Detect change between Rooms and Packages tab
    const tabRoomsPaackages = document.querySelector('.AccommodationsNav-room-package-btn');
    if (tabRoomsPaackages) {
      let refreshPrice = thn.debounce(function () {
        ctx.widget.update();
      }, 500);

      thn.observeDom(tabRoomsPaackages, refreshPrice, {
        attributes: true,
      });

      thn.pageUnload(function () {
        thn.stopObserveDom(tabRoomsPaackages, refreshPrice);
        refreshPrice = null;
      });
    }

  };

  // priceParseData global variable
  const angular = window.angular;

  // List of hotels PID's to be used for priceParseData_v2
  const ELDENANDALOU_PID = 1036103;
  const isPropertyUsingPackagesPW = [1038477].includes(thn.data.propertyId);

  /**
     * @memberOf module:bs_3
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v1 = function priceParseData_v1(ctx) {

    const widgetData = {};

    if (thn.data.propertyId === ELDENANDALOU_PID) { return {}; }

    if (!angular) { return widgetData; }

    const { promocode } = thn.widgets.price.getPromocode();
    // Don't set "priceSpecial = 1" if we detect that a promocode is being use
    // If do so, the script won't be able to set "priceSpecial = 2" (promocode)
    // and tools with 1click promocode will be continuing appearing
    // The script won't set the priceSpecial to 2 (promocode) if in price_parse_data we give priceSpecial a value
    if (!promocode && (window.location.search.match(/(usertype|discount|identifier|rateplanid|prodid|groupid)=[^&]+/i)
      // In some chains the client wants to flag as "priceSpecial" the search
      // when in the URL parameter "roomtypeid" has a value
      || (thn.parseUrlParameter('roomtypeid') && thn.data.accountUserId === 1033777))) {
      widgetData.priceSpecial = 1;
    }

    // include lang in the PW data
    widgetData.language = thn.data.language;

    const selectors = [
      '[ng-show="room.available"] .CardList-price-title',
      '[ng-if="room.available"] .CardList-price-title',
      '.AccommodationsList-multirate .CardGrid-price-color-change:not(.CardGrid-price--subtitles)',
    ];

    const specificRate = window.location.search.match(/ProdID=[^&]+/);

    let priceEls;
    const seniorDiscountSelector = document.querySelectorAll('.PricingBox-title input:not([aria-label*="Senior Discount"]):not([aria-label*="Pensionärsrabatt"])');
    if (thn.data.accountUserId === 1038104 && !thn.empty(seniorDiscountSelector)) {
      priceEls = [...seniorDiscountSelector].map((el) => el.closest('.PricingBox-option--priceElement'))
        .map((el) => el.querySelector('.CardGrid-price-color-change:not(.CardGrid-price--subtitles)')).filter(thn.isVisible);
    } else {
      priceEls = Array.from(document.querySelectorAll(selectors.join(','))).filter(thn.isVisible);
    }

    // In some property, we need to exclude specific rates
    // To do it, we check the name of the rate, if it doesn't match, we store its price
    if (!specificRate && priceEls.length) {

      priceEls = priceEls.filter((el) => {
        const blackListRegex = engine._getRateBlacklistRegexp();
        const rateName = (el.parentElement.parentElement.querySelector('.multirate-pricelist-rate-title') || el.closest('.CardGrid-wrapper, .CardList-wrapper')?.querySelector('.CardGrid-summary-title, .CardList-summary-title'))?.textContent?.trim();

        if (rateName.match(blackListRegex)) {
          return false;
        }

        // In FirstHotels we need to exclude Member rates
        if (isFirstHotels && el.previousElementSibling?.previousElementSibling?.firstElementChild) {
          return false;
        }

        return true;
      });
    }

    if (!priceEls.length) {
      return widgetData;
    }
    // Get prices from price elements
    let prices = priceEls
      .map((priceEl) => priceEl.textContent.trim())
      .filter((price) => !price.includes('%') && price) // Exclude discount percentages and empty prices
      .map(thn.parseNumber)
      .filter(Boolean);

    // In some properties, we need to make sure that we exclude the undesired rates
    // We can just empty the prices array and obtain them using the rates method
    if ([1027093, 1014051].includes(thn.data.propertyId)) {
      prices = [];
    }

    // Also check the Rate Plans
    // Hotels that should not go through this condition: Emeline
    // If another hotel is added, create an array and add the property ID and Emeline's property ID to the array.
    const isEmeline = thn.data.propertyId === 1037095;
    const rates = document.querySelectorAll('li[ng-repeat="plan in rateAvailPlans"], #AccommodationsNav-filter--rates-id > span');
    if (rates && rates.length && !isEmeline) {
      Array.from(rates).forEach(function (el) {
        // Exclude Special/Government rates
        if (!(el.textContent || '').match(engine._getRateBlacklistRegexp())) {
          // Get the last price
          const ratePrices = el.querySelectorAll('span[ng-if="plan.available"], .CardGrid-price-color-change.ng-scope');
          if (ratePrices && ratePrices.length) {
            prices.push(thn.parseNumber((ratePrices[ratePrices.length - 1] || {}).textContent));
          }
        }
      });
    }

    // We need to get the price from Best Flexible Rate or Promotion Rate for this property 1027110. It coincides with the one specified in this selector: .CardList-price-title.ng-scope
    // PID 1032273 wants to exclude AAA/AARP, Military and Corporate rates from PW
    const propertiesWithFlexibleRates = [1027110, 1032273];
    if (propertiesWithFlexibleRates.includes(thn.data.propertyId)) {
      const specialPricesEls = document.querySelectorAll('.CardList-price-title.ng-scope');
      prices = Array.from(specialPricesEls).map((el) => thn.parseNumber(el.textContent));
    }

    // Check if the user is seeing rooms filtered by a special rate
    const ratePlanName = document.querySelector('.ratePlanNameText')?.textContent;
    const isBlackListedRate = !!ratePlanName && engine._getRateBlacklistRegexp().test(ratePlanName);
    const isSpecialPrice = isBlackListedRate || engine._isSpecialRateParam();

    if (!promocode && isSpecialPrice) {
      widgetData.priceSpecial = 1;
    }

    widgetData.price = Math.min(...prices);

    /*
      Get searched data from angular object
      Replaces the old datalayer method which was giving the wrong data on page refresh
    */
    const searchData = angular && angular.element(window.document)
      && angular.element(window.document).scope()
      && angular.element(window.document).scope().finalServices;

    if (searchData) {
      // Dates
      widgetData.dateBegin = (searchData.dateIn || '').replace(/-/g, '/');
      widgetData.dateEnd = (searchData.dateOut || '').replace(/-/g, '/');

      if (widgetData.dateBegin && widgetData.dateEnd) {
        widgetData.nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd) || 1;
      }

      // Guests
      widgetData.adults = searchData.adults || 2;
      widgetData.children = searchData.children || 0;

      if (widgetData.children) {
        // Rooms dispo
        const room1Info = {
          adults: widgetData.adults,
          children: widgetData.children,
          childrenAges: [],
        };

        if (!thn.empty(searchData.childrenAges) && (typeof searchData.childrenAges === 'string')) {
          room1Info.childrenAges = (searchData.childrenAges.split(',').map((el) => thn.parseNumber(el) || 10));
        } else {
          for (let i = 0; i <= widgetData.children - 1; i += 1) {
            room1Info.childrenAges.push((searchData.childrenAges || [])[i] || 10);
          }
        }

        widgetData.roomsDispo = [room1Info];
      }
      widgetData.guests = widgetData.adults + widgetData.children;

      // Rooms
      widgetData.rooms = searchData.rooms || 1;
    }

    // Currency
    const localeElement = document.querySelector('#international-language-dropdown-id');
    const currencyMatch = localeElement && localeElement.textContent.match(/\(([A-Z]{3})\)/);

    if (currencyMatch && currencyMatch.length === 2) {
      widgetData.currency = thn.getCurrencyCode(currencyMatch[1]);
    } else {
      // only use this as failsafe (it fails sometimes)
      widgetData.currency = thn.dataLayerHelper.get('ihCurrency');
    }

    // On Penta Hotels, the client wants to return "no-rooms" when one of the promocodes in the blacklist is applied or the user is logged
    // Use this when you want to hide the PW depending of the promocode applied
    const promocodeBlacklist = 'pentapkg1|pentapkg2|pentapkg3|pentapkg4|pentapackage';
    const invalidPromocode = document.querySelector('#add-code-dropdown-id li')?.textContent?.trim().match(new RegExp(promocodeBlacklist, 'i'));
    // List to inhibit the price comparison from being displayed when the groupID parameter appears in the URL.
    const uidsWithGroupIdExclusion = [1033396];
    const params = (new URL(window.location)).searchParams;
    const hidePw = ([1035380].includes(thn.data.accountUserId) && thn.isUserLogged()) || invalidPromocode || (uidsWithGroupIdExclusion.includes(thn.data.accountUserId) && params.get('groupID'));

    if (widgetData.price
      && widgetData.currency
      && widgetData.guests
      && !hidePw
    ) {
      widgetData.set = true;
      widgetData.roomDetails = engine._roomDetailsParseData();
    } else if (hidePw) {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  // The version 2 is for those hotels where R&R guests are collected from the room and not from the search

  /**
     * @memberOf module:bs_3
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v2 = function priceParseData_v2(ctx) {

    const widgetData = {};
    const usersOccupancyBySearch = [1041808].includes(thn.data.accountUserId); // occupancy by search

    if (thn.data.propertyId !== ELDENANDALOU_PID) { return {}; }

    // Dates
    const searchData = angular && angular.element(window.document)
      && angular.element(window.document).scope()
      && angular.element(window.document).scope().finalServices;

    const dateBegin = searchData?.dateIn?.replace(/-/g, '/');
    const dateEnd = searchData?.dateOut?.replace(/-/g, '/');

    let nights;

    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin;
      widgetData.dateEnd = dateEnd;
      nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd) || 1;
    }

    const localeElement = document.querySelector('#international-language-dropdown-id');
    const currencyMatch = localeElement && localeElement.textContent.match(/\(([A-Z]{3})\)/);
    const roomsInfo = [...document.querySelectorAll('.CardList-summary, .AccommodationsGrid-cat-content-wrap')].map((room) => {
      const adults = (!usersOccupancyBySearch && thn.parseNumber(room.querySelector('.IconLabel-label')?.textContent)) || thn.dataLayerHelper.get('ihAdults');
      const children = (usersOccupancyBySearch && thn.dataLayerHelper.get('ihChildren')) || 0;
      const price = thn.parseNumber(room.querySelector('.CardList-price-title')?.textContent);
      if (adults && price) {
        return {
          adults: adults || 2,
          children: children,
          price: price,
          childrenAges: [],
        };
      }
      return false;
    }).filter(Boolean);

    roomsInfo.sort((a, b) => a.price - b.price);

    for (let i = 1; i <= roomsInfo[0]?.children; i += 1) {
      roomsInfo[0].childrenAges.push(5);
    }

    if (roomsInfo.length && nights) {
      widgetData.roomsDispo = [roomsInfo[0]];
      widgetData.price = roomsInfo[0].price;
      widgetData.currency = thn.getCurrencyCode(currencyMatch[1]) || thn.dataLayerHelper.get('ihCurrency');
      widgetData.set = true;
    } else {
      widgetData.set = false;
      widgetData.noRooms = true;
    }

    return widgetData;

  };

  // For properties wanting to use Packages part of R&R
  engine.priceParseData_v3 = function priceParseData_v3() {
    if (!isPropertyUsingPackagesPW && !window.location.toString().match(/package/)) { return {}; }
    const widgetData = {};
    // Dates
    const checkIn = thn.dataLayerHelper.get('ihDate');
    const checkOut = thn.dataLayerHelper.get('ihDateOut');

    if (!checkIn || !checkOut) {
      return null;
    }

    widgetData.dateBegin = checkIn;
    widgetData.dateEnd = checkOut;
    const nights = thn.dataLayerHelper.get('ihNights') || thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);

    // Guests
    widgetData.adults = thn.dataLayerHelper.get('ihAdults') || 2;
    widgetData.children = thn.dataLayerHelper.get('ihChildren') || 0;

    // Prices and Currency
    widgetData.currency = thn.getCurrencyCode(thn.dataLayerHelper.get('ihCurrency'));
    const prices = [...document.querySelectorAll('.CardList-price-title')].map((price) => thn.parseNumber(price.textContent.trim()));
    if (!thn.empty(prices)) {
      widgetData.price = Math.min(...prices);
      widgetData.nights = nights;
      widgetData.priceSpecial = 3;
      widgetData.set = true;
    } else {
      widgetData.set = false;
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     *
     * @memberOf module:bs_3
     *
     */
  engine._roomDetailsParseData = function roomDetailsParseData() {
    const roomDetailsArray = [];
    const roomSelector = document.querySelectorAll('.AccommodationsList-card, .AccommodationsGrid-card');
    const priceInfoSelector = '.PricingBox-option--priceElement.PricingBox-option-multirate, .CardGrid-price, .CardList-price-title-container';
    const breakfastText = /(desayuno|breakfast)/i; // Portuguese uses 'Breakfast'.
    const nonRefundableText = /(no reembolsable|non refundable)/i;
    let roomName,
      roomAdults,
      roomChildren,
      roomType;

    if (roomSelector.length) {
      Array.from(roomSelector).forEach(function (element, idx) {
        const typeRoomSelector = {
          'Junior Suite': 'junior suite',
          'Master Suite': 'master suite',
          'Presidential Suite': 'presidential suite',
          'Superior Room': 'superior room',
          'Executive Room': 'executive room',
          Suite: 'suite',
          Deluxe: 'deluxe',
          Family: 'family',
          Baby: 'baby',
          Double: 'double',
        };
        roomName = (element.querySelector('.CardList-summary-title, .CardGrid-summary-title') || {}).innerText || '';
        roomAdults = thn.parseNumber((element.querySelectorAll('.CardList-summary-offering') || {}).innerText || 2);
        roomChildren = 0; // Rooms only contain number of people (not split into adults and children). So, all goes to adults.
        roomType = (Object.keys(typeRoomSelector).filter((key) => ((roomName)).toLowerCase().includes(typeRoomSelector[key]))[0] || 'Standard');

        const roomData = {
          id: '',
          name: roomName,
          adults: roomAdults,
          children: roomChildren,
          prices: [],
          type: roomType,
        };

        // Iterate Prices
        const priceInfo = element.querySelectorAll(priceInfoSelector);

        if (priceInfo.length) {
          Array.from(priceInfo).forEach(function (priceUnit) {
            const priceItemName = (priceUnit.querySelector('.PricingBox-title .multirate-pricelist-rate-title :not(.merchandisingSalesText), .CardList-price-title') || {}).innerText || '';
            const price = thn.parseNumber((priceUnit.querySelector('.CardGrid-price-color-change') || {}).innerText || '');
            const priceData = {
              id: '',
              name: priceItemName,
              totalNight: price || thn.parseNumber(priceItemName),
              breakfast: priceItemName.match(breakfastText) ? 1 : 0,
              refundable: !priceItemName.match(nonRefundableText) ? 1 : 0,
            };
            roomData.prices.push(priceData);
          });
          roomDetailsArray.push(roomData);
        }
      });
    }
    return roomDetailsArray;
  };

  /**
     * Implements **bookingId**.
     *
     * @memberOf module:bs_3
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};
    const bookingExclusion = () => {
      const pidsWithGroupIdExclusion = [1034767];
      const params = (new URL(window.location)).searchParams;
      if (pidsWithGroupIdExclusion.includes(thn.data.propertyId) && params.get('groupID')) {
        this.logger.warn('Stop BW execution: detected property with groupID exclusion');
        return true;
      }
      return false;
    };

    if (bookingExclusion()) {
      widgetData.set = false;
      return widgetData;
    }

    let price;
    let multiRooms = false;

    // Sana Hotels - avoid logging "Groups Call In" bookings
    if (thn.data.accountUserId === 1032355 && window.location.href.match(/groupid/i)) {
      widgetData.set = false;
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // ID
    const id = document.querySelector('.Confirmation-guest-number');
    if (id) {
      widgetData.bookingId = thn.trim(id.textContent, '#');
    } else {
      const multiId = Array.from(document.querySelectorAll('.Confirmation-user-date > div:first-child > div:first-child')).map(function (el) {
        const idMatch = el.textContent.match(/\d+/);
        return idMatch ? idMatch[0] : '';
      }).filter(function (str) {
        return str && str.trim().length;
      });

      if (multiId.length) {
        widgetData.bookingId = multiId.join(';');
        multiRooms = true;
      }

      if (thn.empty(widgetData.bookingId)) { // Datalayer alternative
        const datalayerBookings = [];
        for (let i = 0; i < 20; i += 1) { // Limit for preventing inifinite loop
          const bookingId = parseInt(thn.dataLayerHelper.get(`transactionProducts.${i}.sku`), 10);
          if (!bookingId) { break; }
          datalayerBookings.push(bookingId);
        }

        if (datalayerBookings.length) {
          widgetData.bookingId = datalayerBookings.join(';');
          multiRooms = true;
        }
      }
    }

    // Dates
    widgetData.dateBegin = thn.dataLayerHelper.get('ihDate');
    if (widgetData.dateBegin) {
      widgetData.dateBegin = widgetData.dateBegin.replace(/-/g, '/');
    }
    widgetData.dateEnd = thn.dataLayerHelper.get('ihDateOut');
    if (widgetData.dateEnd) {
      widgetData.dateEnd = widgetData.dateEnd.replace(/-/g, '/');
    }

    // Promocode
    const promoCode = thn.dataLayerHelper.get('ihDiscountCode') || thn.dataLayerHelper.get('ecommerce.coupon') || thn.widgets.price.getPromocode().promocode || '';

    // Oraculo case
    const isPosadas = thn.data.accountUserId === 1032244;
    const isThistle = thn.data.accountUserId === 1034117;
    const isOraculoCode = !!promoCode.match(/orac|THOFFH|THOFFL/i); // Posadas code: orac | Thistle codes: THOFFH, THOFFL
    if (isOraculoCode) {
      let isOraculoRate;
      if (isPosadas) {
        isOraculoRate = !!document.querySelector('[ng-if="promotionBannerObject.promotionName"]')?.textContent?.match(/tu estancia al mejor precio|best price for you stay/i);
      } else if (isThistle) {
        isOraculoRate = !!document.querySelector('[ng-if="promotionBannerObject.promotionName"]')?.textContent?.match(/best flexible|best flexible breakfast/i);
      }
      widgetData.promocode = isOraculoRate ? promoCode : '';
    } else {
      const priceDiscount = document.querySelector('.strike-through-total.ng-binding.ng-scope');
      widgetData.promocode = priceDiscount ? thn.dataLayerHelper.get('ihDiscountCode') : thn.widgets.price.getPromocode().promocode;
    }

    // Price
    const datalayerPrice = thn.dataLayerHelper.get('ihAmount');
    const datalayerCurrency = thn.dataLayerHelper.get('ihCurrency');
    const currencyTxt = ((document.querySelector('#formatted-reservation-total-id, #room-rate-plan-price-id'))?.textContent)?.trim();
    const currencyHtml = (document.querySelector('select[ng-model=currency]') || {}).value
      || thn.parseCurrency(currencyTxt);

    if ((datalayerPrice && datalayerCurrency) && thn.data.propertyId !== HOTEL_MAHEKAI_PID) {
      widgetData.currency = datalayerCurrency;
      widgetData.price = datalayerPrice;
    } else {
      const prices = Array.from(document.querySelectorAll('.Receipt-footer tr:last-child td:last-child, #multiroom-totalreservation-price')).map(function (el) {
        price = el.textContent;
        if (!price.match(/\bCode\b/)) {
          return thn.parseNumber(price);
        }
        return null;
      }).filter(Boolean);

      if (prices.length && currencyHtml) {
        widgetData.price = Math.max(...prices);
        widgetData.currency = currencyHtml;
      }
    }

    // **********  multiple rooms setup start ************* //
    // return an array of widgetData objects for every room booked
    // this process will store all the rooms as a separate booking
    if (multiRooms) {
      // All the different rooms
      let roomPrices;

      const rooms = thn.dataLayerHelper.get('ihReservations');
      roomPrices = rooms?.map((room) => (room || {}).ihAmount);
      const roomAdults = rooms?.map((room) => room?.ihAdults);
      const roomChildren = rooms?.map((room) => room?.ihChildren);
      const datesBegin = rooms?.map((room) => room?.ihDate?.replace(/-/g, '/'));
      const datesEnd = rooms?.map((room) => room?.ihDateOut?.replace(/-/g, '/'));

      // DOM alternative
      if (thn.empty(roomPrices)) {
        roomPrices = Array.from(document.querySelectorAll('#room-total-price-id')).map((el) => thn.parseNumber((el || {}).textContent));
        widgetData.currency = currencyHtml;
      } else {
        widgetData.currency = datalayerCurrency;
      }

      // All the different bookingId's
      const bookingIds = (widgetData.bookingId || '').split(';');

      // double check that the amount of booking id's are equal to the amount of parsed room prices
      if ((roomPrices || []).length === bookingIds.length) {

        // generate the widgetData objects for each room
        const widgetDataMulti = bookingIds.map((bookingId, idx) => {
          const data = {
            dateBegin: datesBegin[idx] || widgetData.dateBegin,
            dateEnd: datesEnd[idx] || widgetData.dateEnd,
            bookingId: bookingId,
            price: roomPrices[idx],
            adults: roomAdults[idx] || 2,
            children: roomChildren[idx] || 0,
            currency: widgetData.currency,
          };

          if (widgetData.promocode) {
            data.promocode = widgetData.promocode;
          }

          return data;
        });

        return widgetDataMulti;
      }
    }
    // **********  multiple rooms setup end ************* //

    return widgetData;
  };

  return engine;

}

export { factory };
